import { useCallback, useRef, useState, type ReactNode } from "react";
import { CarrierSwitchConfirmDialog } from "./components/CarrierSwitchConfirmDialog";
import { ReshipConfirmDialog } from "./components/ReshipConfirmDialog";
import type { GenerateLabelClickOpts } from "./components/OrderCard";
import {
  downloadOrderBordero,
  downloadOrderLabel,
  generateLabel,
  isCarrierSwitchRequired,
  updateOrderShipping,
} from "./api";
import { prettifyApiError } from "@/shared/api";
import type { Order } from "./types";

export type LabelActions = {
  generateLabel: (
    order: Order,
    opts?: GenerateLabelClickOpts,
    carrierOverride?: string,
    reshipConfirmed?: boolean,
  ) => Promise<void>;
  labelBusyIds: ReadonlySet<string>;
  /** I due dialog (carrier-switch + reship): il consumer li renderizza. null-safe. */
  dialogs: ReactNode;
};

type UseLabelActionsOpts = {
  onRefresh: () => void;
  onMessage: (msg: string | null) => void;
  onError: (msg: string | null) => void;
  isMockMode: boolean;
};

/** Estratto da OrdiniPage (Piano 3 Task 1): flusso "Genera LDV" su singolo
 *  ordine non-AllService, riusabile da qualunque pagina (lista Ordini,
 *  pagina ordine /ordini/:ref). Comportamento invariato rispetto
 *  all'originale — solo i "sink" (messaggio/errore/refresh/isMockMode)
 *  diventano parametri dell'hook invece di state/closure di OrdiniPage. */
export function useLabelActions(opts: UseLabelActionsOpts): LabelActions {
  // Destrutturiamo i sink invece di dipendere dall'intero ``opts`` (un
  // nuovo oggetto letterale ad ogni render del caller): altrimenti
  // ``handleGenerateLabel`` cambierebbe identità ad ogni render di
  // OrdiniPage, ririproducendo il re-render a cascata su tutte le
  // OrderCard che l'Audit 2026-05-13 #18 (``liveOrdersRef``) evitava.
  // ``onMessage``/``onError`` sono in pratica i setState stabili di
  // OrdiniPage; ``onRefresh`` deve restare stabile lato caller (vedi
  // wiring in OrdiniPage, stesso pattern di ``handleDrainComplete``).
  const { onRefresh, onMessage, onError, isMockMode } = opts;
  /** Handler "Genera LDV" su singolo ordine non-AllService (Itamoby/Ityhome/
   *  marketplace direct). Chiama il backend → Poleepo /shippings con
   *  generate_label=True, scarica il PDF in ``data_dir/labels/{ref}.pdf``
   *  e apre il file con l'app default Windows via Electron bridge.
   *  Davide chiede: "usa quelli salvati" (peso/dim dal catalogo) +
   *  "usa esistente" (cache + recovery shipping) + "si" al recovery 500.
   *
   *  Audit 2026-05-12 M-race: ``labelsBusyIds`` traccia gli orderId con
   *  generazione in corso a livello OrdiniPage (parent stabile), evitando
   *  state-leak quando l'utente collassa la card durante il request. La
   *  card vede ``labelBusy={labelsBusyIds.has(order.id)}`` come prop;
   *  remount non resetta il guard. Anche doppio click su card diversa
   *  in parallelo viene gestito senza race. */
  const [labelsBusyIds, setLabelsBusyIds] = useState<Set<string>>(new Set());
  // Gemello di ``usePickupMailAction``: gli id "in volo" vivono in un ref, non
  // dentro l'updater di setState. Assegnare una variabile DENTRO l'updater e'
  // un effetto collaterale (React li vuole PURI e in StrictMode li esegue due
  // volte) e la lettura subito dopo regge solo finche' React valuta l'updater
  // in modo eager. Il ref e' sincrono per contratto; lo stato resta la fonte
  // del render.
  const inFlightRef = useRef<Set<string>>(new Set());

  /** Rilascia il busy di un ordine (gemello di `usePickupMailAction.releaseBusy`).
   *  `prev` invariato se non c'era: niente re-render inutile. */
  const releaseBusy = useCallback((orderId: string) => {
    if (inFlightRef.current.has(orderId)) {
      const next = new Set(inFlightRef.current);
      next.delete(orderId);
      inFlightRef.current = next; // niente mutazione in-place
    }
    setLabelsBusyIds((prev) => {
      if (!prev.has(orderId)) return prev;
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });
  }, []);

  // Switch SDA→Poste Delivery oltre 15 colli (spec 2026-06-24): quando il
  // backend risponde ``needs_carrier_switch`` salviamo l'ordine + gli opts del
  // click qui per ri-chiamare la generazione consentendo lo switch dopo conferma
  // nel dialog. ``null`` = nessun dialog aperto. Mai mutare in-place: ogni
  // apertura/chiusura passa da setState (immutabilità).
  const [carrierSwitch, setCarrierSwitch] = useState<{
    order: Order;
    opts?: GenerateLabelClickOpts;
    packagesCount: number;
    reference: string;
    /** Se true, l'utente ha già confermato il reship (ordine spedito): la
     *  ri-chiamata dopo lo switch corriere non deve ri-chiedere conferma. */
    reshipConfirmed?: boolean;
  } | null>(null);
  // "Rifai LDV" su ordine GIÀ SPEDITO: quando l'utente edita e (ri)genera su un
  // ordine spedito, chiediamo conferma prima di annullare la spedizione Poleepo
  // esistente. ``null`` = nessun dialog aperto.
  const [reshipConfirm, setReshipConfirm] = useState<{
    order: Order;
    opts?: GenerateLabelClickOpts;
    carrierOverride?: string;
  } | null>(null);
  const handleGenerateLabel = useCallback(
    async (
      order: Order,
      opts_?: GenerateLabelClickOpts,
      carrierOverride?: string,
      reshipConfirmed = false,
    ) => {
      // Audit 2026-05-13 #18: il guard NON legge ``labelsBusyIds`` dal
      // closure — la deps ``[labelsBusyIds]`` rendeva ogni
      // ``setLabelsBusyIds`` un trigger di re-render per tutte le OrderCard
      // (callback identity changed) — ma il ref, che e' sincrono. Lo stato
      // serve solo a disegnare il bottone.
      if (inFlightRef.current.has(order.id)) return;
      inFlightRef.current = new Set(inFlightRef.current).add(order.id);
      setLabelsBusyIds((prev) => new Set(prev).add(order.id));
      onMessage(null);
      onError(null);
      // Quando apriamo un dialog di conferma il busy NON si rilascia nel
      // finally: col popup a schermo il bottone tornerebbe abilitato e
      // (raggiunto da Tab) farebbe partire un secondo giro sullo stesso
      // ordine. Lo rilasciano i quattro handler di conferma/annullamento.
      let keepBusy = false;
      try {
        // Rifare la LDV su un ordine GIÀ SPEDITO è distruttivo (annulla la
        // spedizione Poleepo esistente + tracking): chiediamo conferma. force
        // (dirty) = l'utente ha editato colli/peso/dim/corriere → intende
        // rifarla davvero; senza edit è un semplice riuso cache, niente prompt.
        // «Spedito» = ``state``, la stessa regola della guardia backend
        // (``normalize_status(order.status)``). Mai ``raw.status``: sui canali
        // diretti è il vocabolario del marketplace, non quello di OM.
        if (opts_?.force && !reshipConfirmed && order.state === "spedito") {
          setReshipConfirm({ order, opts: opts_, carrierOverride });
          keepBusy = true;
          return;
        }
        // Bug 2026-06-16: se la card ha passato le dimensioni editate, le
        // persistiamo (salva implicito) PRIMA di generare, cosi' generate_label
        // rilegge le misure NUOVE da Order.package_dimensions invece di riusare
        // le vecchie ("continua a rifare quella di prima"). force=true (gia' in
        // opts quando dirty) bypassa la cache LDV e purga la spedizione vecchia.
        if (opts_?.packageDimensions && opts_.packageDimensions.length > 0) {
          await updateOrderShipping(order.id, {
            packages_count: Math.max(1, opts_.packagesCount ?? 1),
            use_pallet: opts_.usePallet ?? false,
            package_dimensions: opts_.packageDimensions,
          });
        }
        const result = await generateLabel(order.id, {
          packages_count: opts_?.packagesCount,
          force: opts_?.force,
          // Corriere: override dello switch SDA→Poste (carrierOverride) ha
          // priorità; altrimenti il corriere scelto nel dropdown della card.
          carrier: carrierOverride ?? opts_?.carrier,
          // Consenso a rifare la LDV su ordine già spedito (dialog confermato).
          reship_when_shipped: reshipConfirmed,
        });
        // SDA + >15 colli senza consenso: il backend NON ha creato la
        // spedizione e propone Poste Delivery Business. Apriamo il dialog di
        // conferma: il busy resta acceso finche' l'utente non sceglie.
        // Il backend resta la guardia autoritativa.
        if (isCarrierSwitchRequired(result)) {
          setCarrierSwitch({
            order,
            opts: opts_,
            packagesCount: result.packages_count,
            reference: result.reference,
            // Preserva il consenso reship: se l'ordine era spedito e l'utente
            // ha già confermato, la ri-chiamata dopo lo switch non deve
            // ri-chiedere conferma (evita doppio dialog).
            reshipConfirmed,
          });
          keepBusy = true;
          return;
        }
        const detail = result.cache_hit
          ? `LDV ${result.reference} riusata (${result.file_name})`
          : `LDV ${result.reference} generata${result.tracking_code ? ` · tracking ${result.tracking_code}` : ""}`;
        // Sugli ordini nativi il backend prova a chiudere l'ordine sul
        // marketplace e a passargli il tracking. È best-effort: se fallisce,
        // il pacco parte comunque ma il CLIENTE resta senza tracking. Il
        // backend l'esito lo restituiva già; qui nessuno lo leggeva, quindi un
        // fallimento era invisibile e l'operatore credeva di aver finito.
        // Non è un errore che annulla la LDV: la LDV c'è. È un lavoro che
        // resta da fare a mano, e va detto come tale.
        const conferma = result.conferma_canale;
        // Scarico della LDV con un anchor ``download``, non con ``window.open``:
        // qui siamo DOPO l'await della generazione, che su Poleepo supera
        // regolarmente i ~5 s di attivazione utente transitoria. Il popup
        // blocker fermava la scheda e ritornava ``null`` SENZA lanciare, quindi
        // nessun catch scattava: restava a video «LDV … generata» e nessun PDF
        // da nessuna parte. Il ramo ``omBridge`` era morto da quando Electron
        // e' stato rimosso (2026-07-17): quel bridge non lo inietta piu' nessuno.
        //
        // Lo scarico si tenta PRIMA di parlare, cosi' il messaggio finale e'
        // uno solo e dice tutta la verita': la LDV c'e' comunque (l'abbiamo
        // appena generata), a fallire e' al massimo il download.
        let scaricoErr: string | null = null;
        try {
          await downloadOrderLabel(order.id);
          // BRT: il borderò serve al ritiro e si scarica insieme alla LDV
          // (Davide 22/10), non solo dal tasto in testata.
          if ((result.carrier || "").toUpperCase().startsWith("BRT")) {
            await downloadOrderBordero(order.id);
          }
        } catch (err) {
          scaricoErr = prettifyApiError(err);
        }
        const problemaCanale =
          conferma && !conferma.confermato && !conferma.gia_fatto && conferma.motivo
            ? `Ma il marketplace NON è stato aggiornato: ${conferma.motivo}`
            : null;
        const problemaScarico = scaricoErr
          ? `Il PDF non è stato scaricato: ${scaricoErr} — riprova con «Scarica LDV» nel dettaglio ordine.`
          : null;
        const problemi = [problemaCanale, problemaScarico].filter(Boolean).join(" ");
        if (problemi) {
          onError(`${detail}. ${problemi}`);
        } else {
          onMessage(detail);
        }
        if (!isMockMode) void onRefresh();
      } catch (err) {
        onError(prettifyApiError(err));
      } finally {
        if (!keepBusy) releaseBusy(order.id);
      }
    },
    [onMessage, onError, onRefresh, isMockMode, releaseBusy],
  );

  // Conferma switch corriere: ri-chiama la generazione consentendo Poste
  // Delivery (carrier override). Chiudiamo il dialog PRIMA di ri-chiamare per
  // evitare doppie aperture; il busy lo gestisce ``handleGenerateLabel``.
  const confirmCarrierSwitch = useCallback(() => {
    const pending = carrierSwitch;
    setCarrierSwitch(null);
    if (pending) {
      // Il busy era tenuto acceso per il dialog: va rilasciato PRIMA della
      // ri-chiamata, altrimenti la guardia di idempotenza la scarta.
      releaseBusy(pending.order.id);
      void handleGenerateLabel(
        pending.order,
        pending.opts,
        "POSTE_DELIVERY",
        pending.reshipConfirmed ?? false,
      );
    }
  }, [carrierSwitch, handleGenerateLabel, releaseBusy]);

  // Annulla switch: nessuna spedizione creata, l'ordine resta su SDA. Feedback
  // esplicito (regola Davide: ogni azione dà sempre un esito leggibile).
  const cancelCarrierSwitch = useCallback(() => {
    if (carrierSwitch) {
      onMessage(
        `LDV non generata per ${carrierSwitch.reference}: l'ordine resta su SDA ` +
          `(riduci i colli sotto 15 o procedi manualmente).`,
      );
      releaseBusy(carrierSwitch.order.id);
    }
    setCarrierSwitch(null);
  }, [carrierSwitch, onMessage, releaseBusy]);

  // Conferma "Rifai LDV" su ordine spedito: ri-chiama la generazione col
  // consenso a purgare la spedizione esistente. Chiudiamo il dialog PRIMA di
  // ri-chiamare; busy/feedback li gestisce ``handleGenerateLabel``.
  const confirmReship = useCallback(() => {
    const pending = reshipConfirm;
    setReshipConfirm(null);
    if (pending) {
      releaseBusy(pending.order.id);
      void handleGenerateLabel(pending.order, pending.opts, pending.carrierOverride, true);
    }
  }, [reshipConfirm, handleGenerateLabel, releaseBusy]);

  // Annulla il reship: la spedizione esistente resta invariata. Feedback
  // esplicito (regola Davide: ogni azione dà sempre un esito leggibile).
  const cancelReship = useCallback(() => {
    if (reshipConfirm) {
      onMessage(
        `LDV non rifatta per ${reshipConfirm.order.id}: ` +
          `la spedizione esistente resta invariata.`,
      );
      releaseBusy(reshipConfirm.order.id);
    }
    setReshipConfirm(null);
  }, [reshipConfirm, onMessage, releaseBusy]);

  const dialogs = (
    <>
      {/* Conferma switch SDA→Poste Delivery oltre 15 colli (singolo ordine):
          il backend ha rifiutato SDA per troppi colli e propone Poste Delivery
          Business. Conferma → ri-genera con quel corriere; Annulla → l'ordine
          resta su SDA. */}
      {carrierSwitch && (
        <CarrierSwitchConfirmDialog
          mode="single"
          items={[
            { reference: carrierSwitch.reference, packages_count: carrierSwitch.packagesCount },
          ]}
          onConfirm={confirmCarrierSwitch}
          onCancel={cancelCarrierSwitch}
        />
      )}

      {/* Conferma "Rifai LDV" su ordine GIÀ SPEDITO: annulla la spedizione
          Poleepo esistente e la ricrea coi dati/corriere editati. */}
      {reshipConfirm && (
        <ReshipConfirmDialog
          reference={reshipConfirm.order.id}
          onConfirm={confirmReship}
          onCancel={cancelReship}
        />
      )}
    </>
  );

  return { generateLabel: handleGenerateLabel, labelBusyIds: labelsBusyIds, dialogs };
}
